import styles from "../../../styles/components/Modal/Affiliate/AddAffiliate.module.css";
import React, { useState, useRef, useEffect, useContext } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { InputSelector } from "../../UI/InputSelector";
import { DropdownSelector } from "../../UI/DropdownSelector";
import { MultiSelectDropdown } from "../../UI/MultiSelectDropdown";
import { addAffiliateApi } from "../../../utils/api/Affiliate/affiliateAddApi";
import { getAffiliateFields } from "../../../utils/api/Affiliate/affiliateFields";
import { getAllDesksApi } from "../../../utils/api/Admin/desk/getAllDesksApi";
import { getAdmins } from "../../../utils/api/Admin/getAdminsApi";
import { getSaleStatuses } from "../../../utils/api/Lead/getSaleStatusesApi";
import { LangContext } from "../../../context/LangContext";
import { ThemeContext } from "../../../context/ThemeContext";

const translations = {
  ru: {
    loadingDesksError: "Не удалось загрузить список десков",
    loadingManagersError: "Не удалось загрузить список менеджеров",
    loadingSaleStatusesError: "Не удалось загрузить список статусов продаж",
    unknownError: "Произошла неизвестная ошибка",
    title: "Добавить Аффилиата",
    confirm: "Подтвердить"
  },
  en: {
    loadingDesksError: "Failed to load desk list",
    loadingManagersError: "Failed to load manager list",
    loadingSaleStatusesError: "Failed to load sale statuses",
    unknownError: "An unknown error occurred",
    title: "Add Affiliate",
    confirm: "Confirm"
  },

};

export const AddAffiliate = ({ handleAddAffiliate, fetchAffiliates }) => {
  const { langSelected } = useContext(LangContext);
  const { theme } = useContext(ThemeContext);
  const t = translations[langSelected] || translations.ru;
  const affiliateFields = getAffiliateFields(langSelected);

  const [formData, setFormData] = useState({
    affiliate: "",
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    sale_status: "New",
    desk_id: null,
    admin_ids: [],
  });
  const [focused, setFocused] = useState({});
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const [deskOptions, setDeskOptions] = useState([]);
  const [loadingDesks, setLoadingDesks] = useState(true);
  const [deskError, setDeskError] = useState(null);

  const [managerOptions, setManagerOptions] = useState([]);
  const [loadingManagers, setLoadingManagers] = useState(true);
  const [managerError, setManagerError] = useState(null);

  const [saleStatusOptions, setSaleStatusOptions] = useState([]);
  const [loadingSaleStatuses, setLoadingSaleStatuses] = useState(true);
  const [saleStatusError, setSaleStatusError] = useState(null);

  const modalRef = useRef(null);

  const handleClickOutside = (event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      handleAddAffiliate();
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleAddAffiliate]);

  useEffect(() => {
    const fetchData = async () => {
      setLoadingDesks(true);
      setLoadingManagers(true);
      setLoadingSaleStatuses(true);
      setDeskError(null);
      setManagerError(null);
      setSaleStatusError(null);

      try {
        const [desksResult, adminsResult, saleStatusesResult] = await Promise.all([
          getAllDesksApi(),
          getAdmins(1, 50),
          getSaleStatuses(),
        ]);

        setDeskOptions(desksResult.data.map(desk => ({ value: desk.id, label: desk.name })));
        setLoadingDesks(false);

        setManagerOptions(adminsResult.data.map(admin => ({
          value: String(admin.id),
          label: admin.first_name ? `${admin.first_name} ${admin.last_name || ''}`.trim() : admin.email
        })));
        setLoadingManagers(false);

        setSaleStatusOptions((saleStatusesResult.data || []).map(status => ({ value: status, label: status })));
        setLoadingSaleStatuses(false);

      } catch (error) {
        if (error.response?.url.includes('desk')) {
          setDeskError(error.message || t.loadingDesksError);
        } else if (error.response?.url.includes('admin')) {
          setManagerError(error.message || t.loadingManagersError);
        } else if (error.response?.url.includes('sale-statuses')) {
          setSaleStatusError(error.message || t.loadingSaleStatusesError);
        } else {
          setDeskError(t.unknownError);
          setManagerError(t.unknownError);
          setSaleStatusError(t.unknownError);
        }
        setLoadingDesks(false);
        setLoadingManagers(false);
        setLoadingSaleStatuses(false);
      }
    };

    fetchData();
  }, [t.loadingDesksError, t.loadingManagersError, t.loadingSaleStatusesError, t.unknownError]);

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleFocus = (key) => {
    setFocused((prev) => ({ ...prev, [key]: true }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const handleBlur = (key) => {
    setFocused((prev) => ({ ...prev, [key]: false }));
    setTouched((prev) => ({ ...prev, [key]: true }));
  };

  const handleSubmit = async () => {
    const dataToSend = {};

    for (const key of ['email', 'affiliate', 'password', 'sale_status']) {
      if (formData[key] !== undefined) dataToSend[key] = formData[key];
    }

    if (dataToSend.sale_status === "") {
      dataToSend.sale_status = "New";
    }

    for (const key of ['first_name', 'last_name']) {
      if (formData[key] && formData[key].trim() !== '') {
        dataToSend[key] = formData[key];
      }
    }

    if (formData.desk_id) {
      const deskId = parseInt(formData.desk_id, 10);
      if (!isNaN(deskId)) dataToSend.desk_id = deskId;
    }

    if (Array.isArray(formData.admin_ids)) {
      const adminIds = formData.admin_ids
        .map((adminId) => parseInt(adminId, 10))
        .filter((adminId) => !isNaN(adminId));

      dataToSend.admin_ids = adminIds;
      dataToSend.admin_id = adminIds[0] ?? null;
    }

    try {
      await addAffiliateApi(dataToSend);
      handleAddAffiliate();
      await fetchAffiliates();
    } catch (err) {
      if (err && err.errors) {
        setErrors(err.errors);
      } else if (err && err.message) {
        setErrors({ general: err.message });
      } else {
        setErrors({ general: t.unknownError });
      }
    }
  };

  const isLoadingData = loadingDesks || loadingManagers || loadingSaleStatuses;

  const modalClass = theme === 'dark' ? `${styles.modal} ${styles.darkModal}` : styles.modal;
  const filtersContainerClass = theme === 'dark' ? `${styles.filtersContainer} ${styles.darkFiltersContainer}` : styles.filtersContainer;
  const modalLabelClass = theme === 'dark' ? `${styles.modalLabel} ${styles.darkModalLabel}` : styles.modalLabel;
  const modalContentClass = theme === 'dark' ? `${styles.modalContent} ${styles.darkModalContent}` : styles.modalContent;
  const inputRowLabelClass = theme === 'dark' ? styles.darkLabel : '';
  const textErrorClass = theme === 'dark' ? styles.darkTextError : '';
  const modalFooterClass = theme === 'dark' ? `${styles.modalFooter} ${styles.darkModalFooter}` : styles.modalFooter;
  const submitButtonClass = theme === 'dark' ? `${styles.submitButton} ${styles.darkSubmitButton}` : styles.submitButton;
  const sectionClass = theme === 'dark' ? `${styles.section} ${styles.darkSection}` : styles.section;

  return (
      <div className={modalClass}>
        <div className={filtersContainerClass} ref={modalRef}>
          <div className={modalLabelClass}>
            <p>{t.title}</p>
            <div onClick={handleAddAffiliate}>
              <CloseIcon className={theme === 'dark' ? styles.darkCloseIcon : ''} />
            </div>
          </div>

          <div className={modalContentClass}>
            <div className={sectionClass}>
              {affiliateFields.map(({ key, label, type }) => {

                const isDesk = key === "desk_id";
                const isManager = key === "admin_ids";
                const isSaleStatus = key === "sale_status";

                let dropdownContent;
                let currentError;
                let currentOptions;
                let isLoadingField;
                let selectedValue;

                if (isDesk) {
                  currentError = deskError;
                  currentOptions = deskOptions;
                  isLoadingField = loadingDesks;
                  selectedValue = formData.desk_id;
                } else if (isManager) {
                  currentError = managerError;
                  currentOptions = managerOptions;
                  isLoadingField = loadingManagers;
                  selectedValue = Array.isArray(formData.admin_ids) ? formData.admin_ids : [];
                } else if (isSaleStatus) {
                  currentError = saleStatusError;
                  currentOptions = saleStatusOptions;
                  isLoadingField = loadingSaleStatuses;
                  selectedValue = formData.sale_status;
                }

                if (type === "dropdown") {
                  if (currentError) {
                    dropdownContent = <div className={`${styles.textError} ${textErrorClass}`}>{currentError}</div>;
                  } else {
                    dropdownContent = (
                        <DropdownSelector
                            filterName={key}
                            selectedValue={selectedValue}
                            handleFilterChange={handleChange}
                            onFocus={() => handleFocus(key)}
                            onBlur={() => handleBlur(key)}
                            hasError={!!errors[key]}
                            errorMessage={errors[key]}
                            isFocused={focused[key]}
                            options={currentOptions}
                            isLoading={isLoadingField}
                            theme={theme}
                        />
                    );
                  }
                } else if (type === "multiDropdown") {
                  if (currentError) {
                    dropdownContent = <div className={`${styles.textError} ${textErrorClass}`}>{currentError}</div>;
                  } else {
                    dropdownContent = (
                        <MultiSelectDropdown
                            filterName={key}
                            selectedValue={selectedValue}
                            handleFilterChange={handleChange}
                            onFocus={() => handleFocus(key)}
                            onBlur={() => handleBlur(key)}
                            hasError={!!errors[key]}
                            errorMessage={errors[key]}
                            options={currentOptions}
                            shouldAutoClearOnMissingOptions={false}
                            isSearchable
                            closeOnSelect={false}
                        />
                    );
                  }
                }

                return (
                    <div key={key} className={styles.inputRow}>
                      <label className={inputRowLabelClass}>{label}:</label>
                      {type === "input" ? (
                          <InputSelector
                              filterName={key}
                              selectedValue={formData[key]}
                              handleFilterChange={handleChange}
                              onFocus={() => handleFocus(key)}
                              onBlur={() => handleBlur(key)}
                              hasError={!!errors[key]}
                              errorMessage={errors[key]}
                              isFocused={focused[key]}
                              type={key.includes('password') ? 'password' : 'text'}
                              theme={theme}
                          />
                      ) : type === "dropdown" || type === "multiDropdown" ? (
                          dropdownContent
                      ) : null}
                    </div>
                );
              })}
              {errors.general && (
                  <div className={`${styles.textError} ${textErrorClass}`}>{errors.general}</div>
              )}
            </div>
          </div>

          <div className={modalFooterClass}>
            <div className={styles.confirmationWrapper}>
              <button
                  className={submitButtonClass}
                  onClick={handleSubmit}
                  disabled={isLoadingData}
              >
                {t.confirm}
              </button>
            </div>
          </div>
        </div>
      </div>
  );
};
